import React, { useCallback, useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { FieldErrors, SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";

import type { ReferringDoctorResult } from "~/api/referringDoctors";
import { HipaaLogo } from "~/icons";
import {
  REFERRAL_REQUIRES_DETAILS,
  referredBySchema,
} from "~/schemas/SignUp/ReferredBy";
import { Dropdown } from "~/shared/components/ui/Dropdown";
import { useFunnelTracking } from "~/shared/hooks/useFunnelTracking";
import { SignUpFormProps } from "~/shared/utils/types";
import { useSignUpStore } from "~/stores";
import { Input } from "~/ui";
import { REFERRED_BY_VALUES, tw } from "~/utils";
import { ActionButtons } from "../ActionButtons";
import { ErrorMessage } from "../ErrorMessage";
import { FormContentContainer } from "../FormContentContainer";
import { ReferralTypeahead } from "./ReferralTypeahead";

const referredByOptions: { value: string; label: string }[] = Object.keys(
  REFERRED_BY_VALUES,
).map((key) => ({
  value: key as keyof typeof REFERRED_BY_VALUES,
  label: REFERRED_BY_VALUES[key as keyof typeof REFERRED_BY_VALUES],
}));

export const ReferredByForm = ({
  onPrev,
  onNext,
  stepId = "referred_by",
}: SignUpFormProps & { stepId?: string }) => {
  const { patient, setPatientField } = useSignUpStore();
  const { referral, otherReferral } = patient;

  const defaultValue = referral;
  const { trackComplete, trackBack, trackErrors } = useFunnelTracking({
    step: "referred_by",
  });
  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<z.infer<typeof referredBySchema>>({
    mode: "onBlur",
    resolver: zodResolver(referredBySchema),
    defaultValues: {
      referredBy: defaultValue ?? "",
      other: otherReferral ?? "",
    },
  });

  const selectedReferral = watch("referredBy");
  const otherValue = watch("other");
  const [showInput, setShowInput] = useState(
    REFERRAL_REQUIRES_DETAILS.includes(selectedReferral as any),
  );

  useEffect(() => {
    const shouldShowInput = REFERRAL_REQUIRES_DETAILS.includes(
      selectedReferral as any,
    );
    setShowInput(shouldShowInput);

    if (!shouldShowInput) {
      setValue("other", "", { shouldValidate: true });
    }

    if (selectedReferral !== "doctorOrHealthcareProvider") {
      setPatientField("referringPracticeId", null);
      setPatientField("referringDoctorId", null);
      setPatientField("referringDoctorName", null);
    }
  }, [selectedReferral, setValue, setPatientField]);

  const handleDoctorSelect = useCallback(
    (result: ReferringDoctorResult | null) => {
      if (result) {
        setPatientField("referringPracticeId", result.practice_id);
        setPatientField("referringDoctorId", result.doctor_id);
        setPatientField(
          "referringDoctorName",
          result.doctor_name ?? result.practice_name,
        );
      } else {
        setPatientField("referringPracticeId", null);
        setPatientField("referringDoctorId", null);
        setPatientField("referringDoctorName", null);
      }
    },
    [setPatientField],
  );

  useEffect(() => {
    setValue("referredBy", defaultValue ?? "");
  }, [defaultValue, setValue]);

  const getInputPlaceholder = () => {
    switch (selectedReferral) {
      case "doctorOrHealthcareProvider":
        return "Which doctor or healthcare provider?";
      case "worksiteOrEmployer":
        return "Which worksite or employer?";
      case "socialMedia":
        return "Which social media outlet?";
      case "other":
        return "If other, please specify";
      default:
        return "";
    }
  };

  const onSubmit: SubmitHandler<z.infer<typeof referredBySchema>> = (data) => {
    setPatientField("referral", data.referredBy);
    setPatientField("otherReferral", data.other || undefined);
    trackComplete({ referral_source: data.referredBy });

    onNext();
  };

  const onError = (errors: FieldErrors) => {
    trackErrors(errors);
  };

  const handlePrev = () => {
    setPatientField("referral", "");
    setPatientField("otherReferral", undefined);
    setPatientField("referringPracticeId", null);
    setPatientField("referringDoctorId", null);
    setPatientField("referringDoctorName", null);
    trackBack();
    onPrev();
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit, onError)}
      className="rounded-lg shadow-xl"
    >
      <FormContentContainer stepId={stepId}>
        <HipaaLogo className="size-20 self-center" />
        <div>
          <h1 className="font-display text-2xl">
            How did you hear about Sarah Lynn Nutrition?
          </h1>
        </div>
        <div className={tw("flex w-full flex-col gap-5", !errors && "mb-8")}>
          <Dropdown
            control={control}
            options={referredByOptions}
            {...register("referredBy")}
          />
          {showInput &&
            (selectedReferral === "doctorOrHealthcareProvider" ? (
              <ReferralTypeahead
                id="other"
                value={otherValue ?? ""}
                onChange={(val) =>
                  setValue("other", val, { shouldValidate: true })
                }
                onSelect={handleDoctorSelect}
                placeholder={getInputPlaceholder()}
              />
            ) : (
              <Input
                containerClassName="w-full"
                placeholder={getInputPlaceholder()}
                id="other"
                compact
                {...register("other")}
              />
            ))}
        </div>
        {errors.referredBy && (
          <ErrorMessage message={errors.referredBy.message ?? ""} />
        )}
        {errors.other && <ErrorMessage message={errors.other.message ?? ""} />}
      </FormContentContainer>
      <ActionButtons onPrev={handlePrev} />
    </form>
  );
};
